/**
 * Detalhe da carga tributária, como na página da origem (`detalheCarga`): o percentual
 * sozinho não diz qual tributo pesou nem se foi bem contabilizado. Abre nas contas de
 * resultado que formam a conta (deduções, despesas tributárias e IRPJ/CSLL), com o peso
 * de cada uma sobre a receita, e no cruzamento 3 (apurado × contabilizado × pago).
 * Sem React, para ter teste. Nenhum número é calculado além de somas de exibição.
 */
import { Dre, IContaDaDre, ICruzamento } from '../../../../hooks/useFechamentoContabilMensal';

export const LINHAS_DA_CARGA: Record<string, string> = {
  deducoes: 'Deduções e impostos sobre vendas',
  despesas_tributarias: 'Despesas tributárias',
  ir_csll: 'IRPJ e CSLL',
};

export interface IBlocoDaCarga {
  linha: string;
  nome: string;
  mes: number;
  ano: number;
  contas: IContaDaDre[];
}

export interface ITributoDaCarga {
  nome: string;
  apurado: number | null;
  contabilizado: number | null;
  pago: number | null;
}

export interface ICarga {
  totalMes: number;
  totalAno: number;
  receitaMes: number;
  receitaAno: number;
  cargaMes: number | null;
  cargaAno: number | null;
  blocos: IBlocoDaCarga[];
  tributos: ITributoDaCarga[];
  /** tributos com o apurado igual ao contabilizado */
  conferidos: number;
}

const soma = (itens: number[]) => itens.reduce((s, v) => s + Math.abs(v || 0), 0);

export const montarCarga = (
  dre: { mes: Dre; ano: Dre },
  contas: IContaDaDre[],
  cruzamentos: ICruzamento[],
): ICarga => {
  const { mes, ano } = dre;
  const total = (d: Dre) => -((d.deducoes || 0) + (d.despesas_tributarias || 0) + (d.ir_csll || 0));
  const totalMes = total(mes);
  const totalAno = total(ano);
  const receitaMes = mes.receita_bruta || 0;
  const receitaAno = ano.receita_bruta || 0;

  const blocos = Object.keys(LINHAS_DA_CARGA)
    .map(linha => {
      const doGrupo = contas.filter(c => c.linha === linha && (c.mes || c.ano));
      return {
        linha,
        nome: LINHAS_DA_CARGA[linha],
        mes: soma(doGrupo.map(c => c.mes)),
        ano: soma(doGrupo.map(c => c.ano)),
        contas: doGrupo,
      };
    })
    .filter(b => b.contas.length);

  // cruzamento 3: uma linha por tributo, juntando "apurado × contabilizado" e "× pago"
  const porTributo = new Map<string, ITributoDaCarga>();
  cruzamentos
    .filter(c => c.numero === 3)
    .forEach(c => {
      const nome = c.nome.split(' apurado')[0];
      const alvo = porTributo.get(nome) ?? {
        nome,
        apurado: c.valor_a,
        contabilizado: null,
        pago: null,
      };
      if (c.nome.includes('contabilizado')) alvo.contabilizado = c.valor_b;
      if (c.nome.includes('pago')) alvo.pago = c.valor_b;
      porTributo.set(nome, alvo);
    });
  const tributos = Array.from(porTributo.values());

  return {
    totalMes,
    totalAno,
    receitaMes,
    receitaAno,
    cargaMes: receitaMes ? totalMes / receitaMes : null,
    cargaAno: receitaAno ? totalAno / receitaAno : null,
    blocos,
    tributos,
    conferidos: tributos.filter(t => t.contabilizado != null && t.apurado === t.contabilizado)
      .length,
  };
};
